import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { UploadChangesComponent } from '../upload-changes/upload-changes.component';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  ClearBidSheet,
  FetchBidItemListWithChangeOrderRequest,
  FetchBidItemsListRequest,
  FetchBidSheetRequest,
  FetchChangeOrderFilterRequest,
  UploadChangeOrderFileRequest,
} from '../../../../../../core/projects.actions';
import { bidItemsListInfo, bidSheet, changeOrderFilter, projectDetails } from '../../../../../../core/projects.selectors';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ProjectsService } from '../../../../../../core/projects.service';
import { BidItemsSideNavScreenComponent } from '../bidItemsSideNav/bid-items-side-nav-screen/bid-items-side-nav-screen.component';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { SuccessComponent } from '../success-component/success.component';
import * as $ from 'jquery';
import { FormBuilder, FormControl } from '@angular/forms';

@Component({
    selector: 'app-bid-items-list',
    templateUrl: './bid-items-list.component.html',
    styleUrls: ['./bid-items-list.component.scss', '../../../../../../../../../styles.projects.scss'],
    standalone: false
})
export class BidItemsListComponent implements OnDestroy {
  @ViewChild('fileUploadRef') fileUploadRef: ElementRef;
  @ViewChild('sortSelect') sortSelect: MatSelect;
  acceptedFileTypes = [
    'application/vnd.ms-excel', // XLS
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX,
    'text/csv',
  ];
  allColumns: string[] = ['item', 'quantity', 'coQty', 'unitPrice', 'uom'];
  displayedColumns: string[] = ['item', 'quantity', 'coQty', 'unitPrice', 'uom'];
  dataSource: any = [];
  projectId: string;
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 20,
  };
  currentPage = 1;
  totalRecords: number | undefined;
  bidSheetId: any;
  file: any;
  form = this.fb.group({
    changeOrder: new FormControl({ value: null, disabled: false }),
    searchText: new FormControl(''),
  });
  /** Trimmed search term; server-side in the Current view, client-side when a change order is selected. */
  searchTerm = '';
  selectedValue: any = {};
  changeOrderFilter: any[] = [];
  isOpen = false;
  selectedRow: any;
  qp: any;
  itemsPerPage = 20;
  originalDataSource: any;
  defFilterValue = JSON.stringify({
    id: -1,
    value: 'Current',
    order_number: 'Current',
  });
  changeOrderMode = 'direct';
  downloadingCsv = false;
  /** Project number (or name) used to label the downloaded CSV. */
  projectLabel = '';
  selectedSortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  sortByColumns: string[] = ['Item', 'Quantity', 'Unit price', 'UOM'];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private fb: FormBuilder,
  ) {
    this.projectId = this.route?.snapshot?.paramMap?.get('projectId');

    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      this.qp = qp;

      // Fetch when on bid sheet tab, or when subTab is absent (side panel closed, tab still visible)
      if (qp['subTab'] === 'change-orders' || (!qp['subTab'] && qp['tab'] === 'mtco')) {
        const payload = { projectId: this.projectId };
        this.store.dispatch(FetchBidSheetRequest({ payload }));
        this.store.dispatch(FetchChangeOrderFilterRequest({ payload }));
      } else if (qp['subTab'] && qp['subTab'] !== 'change-orders') {
        // Only reset when explicitly switching to a different sub-tab (CIWR, COR)
        this.store.dispatch(ClearBidSheet());
        this.bidSheetId = null;
        this.selectedValue = JSON.parse(this.defFilterValue);
        this.dataSource = [];
        this.originalDataSource = [];
      }
    });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        if (project && Object.keys(project).length) {
          this.changeOrderMode = project?.source_entity_extra_attributes?.change_order_mode || 'direct';
          this.projectLabel = project?.number || project?.name || '';
        }
      });

    this.store.select(bidSheet)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.id && data.id !== this.bidSheetId) {
          this.bidSheetId = data.id;
          // Bid sheet loaded — fetch bid items if we're on this tab and haven't loaded yet
          if ((this.qp?.['subTab'] === 'change-orders' || (!this.qp?.['subTab'] && this.qp?.['tab'] === 'mtco')) && !this.dataSource?.length) {
            this.fetchBidItems();
          }
        }
      });

    this.store.select(bidItemsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.list.length > 0) {
          this.originalDataSource = data?.list;
          this.dataSource = data?.list;
          this.totalRecords = data?.totalRecords;

          if (this.selectedValue?.id !== -1) {
            this.paginateData();
          }
        } else if (data) {
          // e.g. a search with no matches: clear the previous page instead of leaving it on screen
          this.originalDataSource = [];
          this.dataSource = [];
          this.totalRecords = 0;
        }
      });

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(value => {
        this.searchTerm = (value || '').trim();
        this.currentPage = 1;
        this.pagination.pageIndex = 0;
        this.projectsService.changeCurrentPageNumber.next(1);
        if (this.selectedValue?.id === -1) {
          this.fetchBidItems();
        } else {
          this.paginateData(true);
        }
      });

    this.store.select(changeOrderFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.changeOrderFilter = [
          {
            key: this.defFilterValue,
            id: -1,
            value: 'Current',
            order_number: 'Current',
          },
          ...data?.list.map(o => ({
            key: JSON.stringify(o),
            ...o,
            order_number: o.is_baseline ? (o.order_number ? `Original - ${o.order_number}` : 'Original') : o.order_number,
          })),
        ];

        this.form.controls.changeOrder.setValue(this.defFilterValue);
      });

    this.commonService.closeDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dialog.closeAll();
        this.store.dispatch(FetchChangeOrderFilterRequest({ payload: { projectId: this.projectId } }));
      });

    this.commonService.openSuccessDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.openSuccessDialog();
      });

    this.form.controls.changeOrder.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.selectedValue = JSON.parse(data);
        this.currentPage = 1;
        this.pagination.pageIndex = 0;
        // Show CO Qty column only in Current view
        this.displayedColumns = this.selectedValue?.id === -1
          ? this.allColumns
          : this.allColumns.filter(c => c !== 'coQty');
        if (this.selectedValue?.id !== -1) {
          const payload = {
            projectId: this.projectId,
            changeOrderId: this.selectedValue.id,
            qp: {
              include: ['bid_items'],
            },
          };
          this.store.dispatch(FetchBidItemListWithChangeOrderRequest({ payload }));
        } else {
          this.fetchBidItems();
        }
      });

    this.commonService.clearRowSelection.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.selectedRow = null;
    });
  }


  rowClicked(data) {
    this.selectedRow = data;

    if (data?.id) {
      data = {
        ...data,
        bid_item_id: data?.id,
      };
    }

    this.projectsService.openRightPanel.next({
      component: BidItemsSideNavScreenComponent,
      data: {
        ...data,
        projectId: this.projectId,
        changeOrderFilterSelectedValue: this.selectedValue,
      },
    });
  }

  downloadCsv(): void {
    if (!this.bidSheetId || this.downloadingCsv) {
      return;
    }
    this.downloadingCsv = true;
    this.projectsService
      .downloadBidSheetCsv(this.bidSheetId, this.projectLabel)
      .pipe(
        finalize(() => (this.downloadingCsv = false)),
        takeUntil(this.onDestroy),
      )
      .subscribe({
        error: () => this.commonService.notification('Unable to download the bid sheet. Please try again.', 'danger'),
      });
  }

  onUploadChangesClicked() {
    this.dialog.open(UploadChangesComponent, {
      disableClose: false,
      data: { x: 3 },
      panelClass: 'floating-dialog-box',
    });
  }

  openSuccessDialog() {
    this.dialog.open(SuccessComponent, {
      disableClose: false,
      data: { x: 3 },
      panelClass: 'floating-dialog-box',
    }).afterClosed().subscribe(() => {
      this.fetchBidItems();
    });
  }

  import(id) {
    $(`#${id}`).click();
  }

  handleFileUpload() {
    this.file = this.fileUploadRef.nativeElement.files[0];
    if (this.file) {
      if (!this.acceptedFileTypes.includes(this.file?.type)) {
        this.commonService.notification('Invalid file type.', 'danger');
        return;
      }

      const payload = {
        inputFile: this.file,
        bidSheetId: this.bidSheetId,
      };

      this.store.dispatch(UploadChangeOrderFileRequest({ payload }));
    }

    this.fileUploadRef.nativeElement.value = '';
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;


    if (this.selectedValue.id !== -1) {
      this.paginateData();
    } else {
      this.fetchBidItems();
    }
  }

  fetchBidItems() {
    const payload = {
      bidSheetId: this.bidSheetId,
      qp: {
        start: this.pagination?.pageIndex * this.pagination?.pageSize,
        total: this.pagination?.pageSize,
        search: this.searchTerm ? encodeURIComponent(this.searchTerm) : undefined,
        include: [
          'sites',
        ],
      },
    };

    this.store.dispatch(FetchBidItemsListRequest({ payload }));
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    // Current view: the API already searched and paginated, so only the loaded page is sorted here.
    // Change-order view: the full list is loaded, so search and paginate it client-side.
    const changeOrderView = this.selectedValue?.id !== -1;
    const rows = changeOrderView ? this.filterBySearch(this.originalDataSource ?? []) : (this.originalDataSource ?? []);
    const sorted = this.sortItems(rows);
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, sorted.length);

    if (changeOrderView) {
      this.totalRecords = sorted.length;
    }
    this.dataSource = sorted.slice(startIndex, endIndex);
  }

  clearSearch(): void {
    this.form.controls.searchText.setValue('');
  }

  /** Client-side counterpart of the API `search` param, for the change-order view that is loaded in full. */
  private filterBySearch(items: any[]): any[] {
    const term = this.searchTerm.toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter(
      row =>
        String(row?.item ?? '').toLowerCase().includes(term) ||
        String(row?.description ?? '').toLowerCase().includes(term)
    );
  }

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    this.sortSelect?.open?.();
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event.value;
    this.sortDirection = 'asc';
    this.paginateData(true);
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
    this.sortDirection = 'asc';
    this.paginateData(true);
  }

  private sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const itemsToSort = [...items];

    const safeLower = (v: any) => String(v ?? '').toLowerCase();
    // Natural order so A2 sorts before A10. (Subtracting item strings gave NaN and left API order.)
    const byItemNo = (a: { item?: unknown }, b: { item?: unknown }) =>
      safeLower(a?.item).localeCompare(safeLower(b?.item), undefined, { numeric: true, sensitivity: 'base' });

    if (!this.selectedSortColumn) {
      return itemsToSort.sort(byItemNo);
    }

    const sortFns: Record<string, (a: any, b: any) => number> = {
      'Item': byItemNo,
      'Quantity': (a, b) => (a.quantity ?? 0) - (b.quantity ?? 0),
      'Unit price': (a, b) => (parseFloat(a.unit_price) || 0) - (parseFloat(b.unit_price) || 0),
      'UOM': (a, b) => safeLower(a.uom).localeCompare(safeLower(b.uom)),
    };

    const fn = sortFns[this.selectedSortColumn];
    if (!fn) {
      return itemsToSort.sort(byItemNo);
    }

    const applyDirection = (result: number) => (this.sortDirection === 'asc' ? result : -result);

    return itemsToSort.sort((a, b) => {
      const primary = fn(a, b);
      if (primary !== 0) {
        return applyDirection(primary);
      }
      return byItemNo(a, b);
    });
  }

  isEllipsisActive(el: HTMLElement): boolean {
    return el ? el.scrollWidth > el.clientWidth : false;
  }

  hasSiteQuantityChanges(row: any): boolean {
    return row?.sites?.some(s => s.previous_value?.length > 0) || false;
  }

  changeOrderFilterItemClicked(value: any) {
    this.isOpen = false;
    this.form.controls.changeOrder.setValue(value?.key);
  }

  ngOnDestroy() {
    this.store.dispatch(ClearBidSheet());
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
